import styled from "styled-components";
import React, { useState, useEffect, useRef } from "react";

const PDFBtn = ({
  width = "36px",
  height = "36px",
  handler,
  isNewsBookMarked,
  widthOfView,
  newsData,
  isAuthed,
  isDisabled,
}) => {
  // Track last click time
  const lastClickTime = useRef(0);
  const ACTIVE_DURATION = 2000; // 2 seconds in ms
  
  // States for UI
  const [currentState, setCurrentState] = useState('default');
  const [isHovering, setIsHovering] = useState(false);
  const isMobile = /Mobi/.test(window.navigator.userAgent);
  
  // Timer for active state
  const activeTimerRef = useRef(null);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (activeTimerRef.current) {
        clearTimeout(activeTimerRef.current);
      }
    };
  }, []);
  
  // Set initial state based on props
  useEffect(() => {
    if (isNewsBookMarked) {
      setCurrentState('active');
    } else {
      setCurrentState('default');
    }
  }, [isNewsBookMarked]);

  // Calculate current visual state
  const getVisualState = () => {
    // If disabled, always show disabled
    if (!isAuthed || isDisabled) {
      return 'disabled';
    }
    
    // If bookmarked, always show active
    if (isNewsBookMarked) {
      return 'active';
    }
    
    // If recently clicked, show active
    const now = Date.now();
    if (now - lastClickTime.current < ACTIVE_DURATION) {
      return 'active';
    }
    
    // Otherwise, respect hover state
    if (isHovering && !isMobile) {
      return 'hover';
    }
    
    // Default state
    return 'default';
  };

  // Handle click
  const handleClick = () => {
    if (!handler || isDisabled || !isAuthed) return;
    
    // Update last click time
    lastClickTime.current = Date.now();
    
    // Force active state
    setCurrentState('active');
    
    // Clear any existing timer
    if (activeTimerRef.current) {
      clearTimeout(activeTimerRef.current);
    }
    
    // Set timer to revert to default after duration
    activeTimerRef.current = setTimeout(() => {
      // Only revert if not bookmarked and still in active state
      if (!isNewsBookMarked && currentState === 'active') {
        setCurrentState(isHovering ? 'hover' : 'default');
      }
    }, ACTIVE_DURATION);
    
    // Call the handler
    handler();
  };

  // Mouse events
  const handleMouseEnter = () => {
    setIsHovering(true);
    
    // Only update state if not in active state and not recently clicked
    const now = Date.now();
    if (currentState !== 'active' && now - lastClickTime.current >= ACTIVE_DURATION) {
      setCurrentState('hover');
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    
    // Only update state if not in active state and not recently clicked
    const now = Date.now();
    if (currentState !== 'active' && now - lastClickTime.current >= ACTIVE_DURATION) {
      setCurrentState('default');
    }
  };

  // Render loading spinner
  const renderLoadingSpinner = () => {
    return (
      <LoadingSpinner>
        <div className="spinner"></div>
      </LoadingSpinner>
    );
  };

  // Render appropriate SVG based on visual state
  const renderSVG = () => {
    const visualState = getVisualState();
    
    switch (visualState) {
      case 'disabled':
        return (
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0_disabled" style={{ maskType: "luminance" }} maskUnits="userSpaceOnUse" x="0" y="0" width="36" height="36">
              <path fillRule="evenodd" clipRule="evenodd" d="M36 18C36 27.9419 27.941 36 18.0006 36C8.05905 36 0 27.9419 0 18C0 8.05815 8.05905 0 18.0006 0C27.941 0 36 8.05815 36 18Z" fill="white"/>
            </mask>
            <g mask="url(#mask0_disabled)">
              <rect width="36" height="36" fill="#E8EBEB"/>
              <circle cx="18" cy="18" r="17" fill="white"/>
            </g>
            <path d="M14.5 15.9266C14.5 17.0003 13.0001 17.0003 13 15.9266L13 11.0003C13 10.5003 13.5 10.0003 14 10.0003C17 9.99997 21 9.99974 24 10.0003C24.5 10.0004 25 10.5004 25 11.0003C25 15.5001 25.0002 22.0003 25 25.0003C25 25.5006 24.5 26.0007 24 26.0007C21.5 26.0007 17 26.0029 14 26.0007C13.5 26.0003 13 25.5003 13 25.0003C13 24.0003 13 24.4631 13 24.0003C13 23.0003 14.5 23.0003 14.5 24.0003C14.5 24.2764 14.7239 24.5003 15 24.5003L23 24.5003C23.2762 24.5003 23.5 24.2764 23.5 24.0003L23.5 12.0003C23.5 11.7241 23.2762 11.5003 23 11.5003L15 11.5003C14.7239 11.5003 14.5 11.7241 14.5 12.0003L14.5 15.9266Z" fill="#C2D0D7"/>
            <path d="M9 17.5H10.6338C11.938 17.5 12.4396 18.3143 12.4396 19.0714C12.4396 19.8286 11.938 20.6429 10.6338 20.6429H10.0892V22.5H9V17.5ZM10.0892 18.45V19.6929H10.5407C11.1282 19.6929 11.3361 19.3929 11.3361 19.0714C11.3361 18.75 11.1282 18.45 10.5407 18.45H10.0892Z" fill="#C2D0D7"/>
            <path d="M13.2897 17.5H15.0596C16.4355 17.5 17.4745 18.5643 17.4745 20C17.4745 21.4643 16.4355 22.5 15.0596 22.5H13.2897V17.5ZM14.3789 21.5357H15.0596C15.9052 21.5357 16.3853 20.9429 16.3853 20C16.3853 19.0857 15.9052 18.4643 15.0596 18.4643H14.3789V21.5357Z" fill="#C2D0D7"/>
            <path d="M21 18.4571H19.3877V19.4143H20.8782V20.3571H19.3877V22.5H18.2985V17.5H21V18.4571Z" fill="#C2D0D7"/>
          </svg>
        );
        
      case 'hover':
        return (
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0_hover" style={{ maskType: "luminance" }} maskUnits="userSpaceOnUse" x="0" y="0" width="36" height="36">
              <path fillRule="evenodd" clipRule="evenodd" d="M36 18C36 27.9419 27.941 36 18.0006 36C8.05905 36 0 27.9419 0 18C0 8.05815 8.05905 0 18.0006 0C27.941 0 36 8.05815 36 18Z" fill="white"/>
            </mask>
            <g mask="url(#mask0_hover)">
              <rect width="36" height="36" fill="#689AB9"/>
              <circle cx="18" cy="18" r="17" fill="white"/>
            </g>
            <path d="M14.5 15.9266C14.5 17.0003 13.0001 17.0003 13 15.9266L13 11.0003C13 10.5003 13.5 10.0003 14 10.0003C17 9.99997 21 9.99974 24 10.0003C24.5 10.0004 25 10.5004 25 11.0003C25 15.5001 25.0002 22.0003 25 25.0003C25 25.5006 24.5 26.0007 24 26.0007C21.5 26.0007 17 26.0029 14 26.0007C13.5 26.0003 13 25.5003 13 25.0003C13 24.0003 13 24.4631 13 24.0003C13 23.0003 14.5 23.0003 14.5 24.0003C14.5 24.2764 14.7239 24.5003 15 24.5003L23 24.5003C23.2762 24.5003 23.5 24.2764 23.5 24.0003L23.5 12.0003C23.5 11.7241 23.2762 11.5003 23 11.5003L15 11.5003C14.7239 11.5003 14.5 11.7241 14.5 12.0003L14.5 15.9266Z" fill="#689AB9"/>
            <path d="M9 17.5H10.6338C11.938 17.5 12.4396 18.3143 12.4396 19.0714C12.4396 19.8286 11.938 20.6429 10.6338 20.6429H10.0892V22.5H9V17.5ZM10.0892 18.45V19.6929H10.5407C11.1282 19.6929 11.3361 19.3929 11.3361 19.0714C11.3361 18.75 11.1282 18.45 10.5407 18.45H10.0892Z" fill="#689AB9"/>
            <path d="M13.2897 17.5H15.0596C16.4355 17.5 17.4745 18.5643 17.4745 20C17.4745 21.4643 16.4355 22.5 15.0596 22.5H13.2897V17.5ZM14.3789 21.5357H15.0596C15.9052 21.5357 16.3853 20.9429 16.3853 20C16.3853 19.0857 15.9052 18.4643 15.0596 18.4643H14.3789V21.5357Z" fill="#689AB9"/>
            <path d="M21 18.4571H19.3877V19.4143H20.8782V20.3571H19.3877V22.5H18.2985V17.5H21V18.4571Z" fill="#689AB9"/>
          </svg>
        );
        
      case 'active':
        return (
          <PDFButtonActiveContainer>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_active" style={{ maskType: "luminance" }} maskUnits="userSpaceOnUse" x="0" y="0" width="36" height="36">
                <path fillRule="evenodd" clipRule="evenodd" d="M36 18C36 27.9419 27.941 36 18.0006 36C8.05905 36 0 27.9419 0 18C0 8.05815 8.05905 0 18.0006 0C27.941 0 36 8.05815 36 18Z" fill="white"/>
              </mask>
              <g mask="url(#mask0_active)">
                <rect width="36" height="36" fill="#00C3FF"/>
                <circle cx="18" cy="18" r="17" fill="white"/>
              </g>
              <path d="M14.5 15.9266C14.5 17.0003 13.0001 17.0003 13 15.9266L13 11.0003C13 10.5003 13.5 10.0003 14 10.0003C17 9.99997 21 9.99974 24 10.0003C24.5 10.0004 25 10.5004 25 11.0003C25 15.5001 25.0002 22.0003 25 25.0003C25 25.5006 24.5 26.0007 24 26.0007C21.5 26.0007 17 26.0029 14 26.0007C13.5 26.0003 13 25.5003 13 25.0003C13 24.0003 13 24.4631 13 24.0003C13 23.0003 14.5 23.0003 14.5 24.0003C14.5 24.2764 14.7239 24.5003 15 24.5003L23 24.5003C23.2762 24.5003 23.5 24.2764 23.5 24.0003L23.5 12.0003C23.5 11.7241 23.2762 11.5003 23 11.5003L15 11.5003C14.7239 11.5003 14.5 11.7241 14.5 12.0003L14.5 15.9266Z" fill="#00C3FF"/>
              <path d="M9 17.5H10.6338C11.938 17.5 12.4396 18.3143 12.4396 19.0714C12.4396 19.8286 11.938 20.6429 10.6338 20.6429H10.0892V22.5H9V17.5ZM10.0892 18.45V19.6929H10.5407C11.1282 19.6929 11.3361 19.3929 11.3361 19.0714C11.3361 18.75 11.1282 18.45 10.5407 18.45H10.0892Z" fill="#00C3FF"/>
              <path d="M13.2897 17.5H15.0596C16.4355 17.5 17.4745 18.5643 17.4745 20C17.4745 21.4643 16.4355 22.5 15.0596 22.5H13.2897V17.5ZM14.3789 21.5357H15.0596C15.9052 21.5357 16.3853 20.9429 16.3853 20C16.3853 19.0857 15.9052 18.4643 15.0596 18.4643H14.3789V21.5357Z" fill="#00C3FF"/>
              <path d="M21 18.4571H19.3877V19.4143H20.8782V20.3571H19.3877V22.5H18.2985V17.5H21V18.4571Z" fill="#00C3FF"/>
            </svg>
            {isNewsBookMarked && renderLoadingSpinner()}
          </PDFButtonActiveContainer>
        );
        
      case 'default':
      default:
        return (
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0_default" style={{ maskType: "luminance" }} maskUnits="userSpaceOnUse" x="0" y="0" width="36" height="36">
              <path fillRule="evenodd" clipRule="evenodd" d="M36 18C36 27.9419 27.941 36 18.0006 36C8.05905 36 0 27.9419 0 18C0 8.05815 8.05905 0 18.0006 0C27.941 0 36 8.05815 36 18Z" fill="white"/>
            </mask>
            <g mask="url(#mask0_default)">
              <rect width="36" height="36" fill="#C2D0D7"/>
              <circle cx="18" cy="18" r="17" fill="white"/>
            </g>
            <path d="M14.5 15.9266C14.5 17.0003 13.0001 17.0003 13 15.9266L13 11.0003C13 10.5003 13.5 10.0003 14 10.0003C17 9.99997 21 9.99974 24 10.0003C24.5 10.0004 25 10.5004 25 11.0003C25 15.5001 25.0002 22.0003 25 25.0003C25 25.5006 24.5 26.0007 24 26.0007C21.5 26.0007 17 26.0029 14 26.0007C13.5 26.0003 13 25.5003 13 25.0003C13 24.0003 13 24.4631 13 24.0003C13 23.0003 14.5 23.0003 14.5 24.0003C14.5 24.2764 14.7239 24.5003 15 24.5003L23 24.5003C23.2762 24.5003 23.5 24.2764 23.5 24.0003L23.5 12.0003C23.5 11.7241 23.2762 11.5003 23 11.5003L15 11.5003C14.7239 11.5003 14.5 11.7241 14.5 12.0003L14.5 15.9266Z" fill="#637282"/>
            <path d="M9 17.5H10.6338C11.938 17.5 12.4396 18.3143 12.4396 19.0714C12.4396 19.8286 11.938 20.6429 10.6338 20.6429H10.0892V22.5H9V17.5ZM10.0892 18.45V19.6929H10.5407C11.1282 19.6929 11.3361 19.3929 11.3361 19.0714C11.3361 18.75 11.1282 18.45 10.5407 18.45H10.0892Z" fill="#637282"/>
            <path d="M13.2897 17.5H15.0596C16.4355 17.5 17.4745 18.5643 17.4745 20C17.4745 21.4643 16.4355 22.5 15.0596 22.5H13.2897V17.5ZM14.3789 21.5357H15.0596C15.9052 21.5357 16.3853 20.9429 16.3853 20C16.3853 19.0857 15.9052 18.4643 15.0596 18.4643H14.3789V21.5357Z" fill="#637282"/>
            <path d="M21 18.4571H19.3877V19.4143H20.8782V20.3571H19.3877V22.5H18.2985V17.5H21V18.4571Z" fill="#637282"/>
          </svg>
        );
    }
  };

  return (
    <PDFBtnWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      id="pdf-exporter"
      style={{ cursor: "pointer", width: width ?? '36px', height: height ?? '36px' }}
      className={isMobile ? 'mobile' : ''}
      data-state={currentState}
    >
      {renderSVG()}
    </PDFBtnWrapper>
  );
};

export default PDFBtn;

const PDFButtonActiveContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  
  .spinner {
    width: 8px;
    height: 8px;
    border: 2px solid rgba(0, 195, 255, 0.3);
    border-radius: 50%;
    border-top-color: #00C3FF;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PDFBtnWrapper = styled.div`
  background: transparent;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const EditButton = styled.img`
  cursor: pointer;
  background-color: transparent;
  color: transparent;
  width: 150px;
  height: 30px;
`;
